import { Controller, useForm } from 'react-hook-form';
import { Button, Flex, Form, Input, Modal } from 'antd';
import type { FC } from 'react';
import { useEffect } from 'react';
import { Text } from '@shared/ui/antd';

import { AGENT_BACKUP_NAME_MAX_LENGTH } from '../../lib/utils';
import styles from './backup-name-modal.module.scss';

interface BackupNameModalProps {
  isOpen: boolean;
  title: string;
  placeholder: string;
  initialValue?: string;
  submitLabel: string;
  cancelLabel: string;
  onSubmit: (title: string) => void | Promise<void>;
  onClose: () => void;
}

type BackupNameFormValues = {
  title: string;
};

export const BackupNameModal: FC<BackupNameModalProps> = ({
  isOpen,
  title,
  placeholder,
  initialValue = '',
  submitLabel,
  cancelLabel,
  onSubmit,
  onClose,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<BackupNameFormValues>({
    mode: 'onChange',
    defaultValues: {
      title: initialValue,
    },
  });

  useEffect(() => {
    if (!isOpen) return;

    reset({
      title: initialValue,
    });
  }, [initialValue, isOpen, reset]);

  const handleCancel = () => {
    reset({
      title: initialValue,
    });
    onClose();
  };

  const handleSubmitForm = async (values: BackupNameFormValues) => {
    await onSubmit(values.title.trim());
    onClose();
  };

  return (
    <Modal
      centered
      open={isOpen}
      onCancel={handleCancel}
      destroyOnClose
      width="auto"
      style={{ background: 'var(--color-bg-muted)', borderRadius: '12px' }}
      cancelButtonProps={{ hidden: true }}
      okButtonProps={{ hidden: true }}
    >
      <Flex vertical gap={20}>
        <Text variant="md" fontWeight={500}>
          {title}
        </Text>

        <Form onFinish={handleSubmit(handleSubmitForm)} className={styles.root} layout="vertical">
          <Form.Item
            name="title"
            help={errors.title?.message}
            validateStatus={errors.title ? 'error' : ''}
            className={styles.field}
          >
            <Controller
              control={control}
              name="title"
              rules={{
                required: true,
                validate: (value) => value.trim().length > 0,
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  autoFocus
                  placeholder={placeholder}
                  className={styles.input}
                  maxLength={AGENT_BACKUP_NAME_MAX_LENGTH}
                  size="large"
                  showCount
                />
              )}
            />
          </Form.Item>

          <Flex gap="12px" align="center" className={styles.actions}>
            <Button onClick={handleCancel} className={styles.actionButton}>
              {cancelLabel}
            </Button>

            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={!isValid || !isDirty || isSubmitting}
              className={styles.actionButton}
            >
              {submitLabel}
            </Button>
          </Flex>
        </Form>
      </Flex>
    </Modal>
  );
};
